import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { getCoachPageUrl } from 'constants/pages';
import { useGetProfileCompletenessProgressLazyQuery } from 'types/generated/client';
import { pluralize } from 'utils/shared/pluralize';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { useModal } from 'hooks/useModal';
import { useViewer } from 'hooks/useViewer';
import classNames from 'styles/utils/classNames';
import CompleteCta from './CompleteCta';
import CompleteListingGuideModal from './CompleteListingGuideModal';
import InvitePlayerGuideModal from './InvitePlayerGuideModal';
import InvitePlayersModal from './InvitePlayersModal';
import PercentageCircle from './PercentageCircle';
import ReviewGuideModal from './ReviewGuideModal';
import UploadReviewsModal from './UploadReviewsModal';
import { ProfileCompletenessSkeleton } from '../Skeletons';

interface ProfileProgress {
  hasInvitations: boolean;
  hasCompleteListing: boolean;
  hasReviews: boolean;
  percentage: number;
}

const ProfileCompleteness = () => {
  const viewer = useViewer();
  const [profileProgress, setProfileProgress] = useState<ProfileProgress>({
    hasInvitations: false,
    hasCompleteListing: false,
    hasReviews: false,
    percentage: 50, // Start with 50% since users reaching this page already have some progress
  });

  const [getProfileCompletenessProgress, { loading, data }] =
    useGetProfileCompletenessProgressLazyQuery();

  const invitePlayerGuideModal = useModal();
  const uploadReviewsGuideModal = useModal();
  const completeListingGuideModal = useModal();

  const invitePlayersModal = useModal();
  const uploadReviewsModal = useModal();

  const { user } = useGetCurrentUser();

  const router = useRouter();

  const separator = (
    <hr className="container mx-auto mt-8 border-color-bg-input-lightmode-primary dark:border-color-bg-input-darkmode-primary max-sm:mt-ds-xl" />
  );

  const pendingInvitationsCount = 6 - (data?.userFollowsAggregate?.aggregate?.count || 0);

  const calculateProfileProgress = (data: any) => {
    if (!data) return;

    const followersCount = data.userFollowsAggregate?.aggregate?.count || 0;
    const userProfile = data.userProfiles?.[0];
    const reviewsCount = data.reviewsAggregate?.aggregate?.count || 0;

    const hasInvitations = followersCount >= 6;
    const hasCompleteListing = Boolean(
      userProfile?.profileImageProviderUrl &&
        userProfile?.aboutMe &&
        userProfile?.coachingStartYear,
    );
    const hasReviews = reviewsCount > 0;

    let earnedPoints = 0;
    if (hasInvitations) earnedPoints += 25;
    if (hasCompleteListing) earnedPoints += 20;
    if (hasReviews) earnedPoints += 10;

    const percentage = Math.round(50 + earnedPoints);

    setProfileProgress({
      hasInvitations,
      hasCompleteListing,
      hasReviews,
      percentage,
    });
  };

  const handleSuccess = () => {
    // Refetch profile completeness data when reviews are uploaded or players are invited
    if (viewer.userId) {
      getProfileCompletenessProgress({
        variables: { userId: viewer.userId },
        fetchPolicy: 'network-only',
      }).then(({ data }) => {
        calculateProfileProgress(data);
      });
    }
  };

  useEffect(() => {
    const fetchProfileProgress = async () => {
      if (viewer.userId) {
        try {
          const { data } = await getProfileCompletenessProgress({
            variables: { userId: viewer.userId },
            fetchPolicy: 'network-only',
          });

          if (data) {
            calculateProfileProgress(data);
          }
        } catch (error) {
          console.error('Error fetching profile progress:', error);
        }
      }
    };

    fetchProfileProgress();
  }, [viewer.userId, getProfileCompletenessProgress]);

  const getGridCols = (length: number) => {
    if (length === 1) return 'grid-cols-1';
    if (length === 2) return 'grid-cols-1 sm:grid-cols-2';
    if (length === 3) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
    return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
  };

  const gridItems = [
    {
      component: <PercentageCircle percentage={profileProgress.percentage} />,
      key: 'percentage',
    },
    {
      component: !profileProgress.hasInvitations ? (
        <CompleteCta
          title={pluralize({
            count: pendingInvitationsCount,
            singular: '1 Invite Player',
            plural: `Invite ${pendingInvitationsCount} Players`,
            hideCount: true,
          })}
          chances={25}
          onInfoClick={() => invitePlayerGuideModal.openModal()}
          onCtaClick={() => invitePlayersModal.openModal()}
          ctaText="Invite Players"
        />
      ) : null,
      key: 'invite',
      show: !profileProgress.hasInvitations,
    },
    {
      component: !profileProgress.hasCompleteListing ? (
        <CompleteCta
          title="Complete your listing"
          chances={10}
          onInfoClick={() => completeListingGuideModal.openModal()}
          onCtaClick={() => router.push(getCoachPageUrl(user?.username || user?.id))}
          ctaText="Complete Listing"
        />
      ) : null,
      key: 'listing',
      show: !profileProgress.hasCompleteListing,
    },
    {
      component: !profileProgress.hasReviews ? (
        <CompleteCta
          title="Upload your reviews"
          chances={15}
          onInfoClick={() => uploadReviewsGuideModal.openModal()}
          onCtaClick={() => uploadReviewsModal.openModal()}
          ctaText="Upload Reviews"
        />
      ) : null,
      key: 'reviews',
      show: !profileProgress.hasReviews,
    },
  ].filter((item) => item.show !== false);

  if (loading) {
    return <ProfileCompletenessSkeleton />;
  }

  // Hide the entire section if profile is 100% complete
  if (profileProgress.percentage >= 100) {
    return null;
  }

  return (
    <>
      {separator}
      <div className="container mx-auto mt-8 max-sm:mt-ds-xl max-sm:px-4">
        <div className="mb-ds-xl flex flex-col gap-1 max-sm:mb-ds-md">
          <h5 className="typography-product-heading-compact-desktop max-sm:typography-product-subheading">
            Profile Completeness
            <span className="ml-2 text-color-brand-primary">{profileProgress.percentage}%</span>
          </h5>
          <span className="max-sm:typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            Complete all steps to unlock your full coaching potential and grow your business.
          </span>
        </div>
        <div
          className={
            'grid  gap-y-10 rounded-3xl border border-color-border-card-lightmode p-ds-3xl shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)] dark:border-color-border-card-darkmode max-sm:gap-y-6 ' +
            getGridCols(gridItems.length)
          }
        >
          {gridItems.map((item, index) => (
            <div
              key={item.key}
              className={classNames(
                'relative border-r border-color-border-card-lightmode dark:border-color-border-card-darkmode max-sm:border-b max-sm:border-r-0',
                index === 1 && 'max-lg:border-r-0',
                index === 3 && 'max-sm:border-b-0',
                index !== gridItems.length - 1 && 'max-sm:pb-6',
                index === gridItems.length - 1 && 'border-r-0 max-sm:border-b-0',
              )}
            >
              {item.component}
            </div>
          ))}
        </div>
      </div>

      <InvitePlayerGuideModal
        isOpen={invitePlayerGuideModal.isOpen}
        handleClose={invitePlayerGuideModal.closeModal}
      />
      <CompleteListingGuideModal
        isOpen={completeListingGuideModal.isOpen}
        handleClose={completeListingGuideModal.closeModal}
      />
      <ReviewGuideModal
        isOpen={uploadReviewsGuideModal.isOpen}
        handleClose={uploadReviewsGuideModal.closeModal}
      />
      <UploadReviewsModal
        isOpen={uploadReviewsModal.isOpen}
        handleClose={uploadReviewsModal.closeModal}
        onSuccess={handleSuccess}
      />
      <InvitePlayersModal
        isOpen={invitePlayersModal.isOpen}
        handleClose={invitePlayersModal.closeModal}
        onSuccess={handleSuccess}
      />
    </>
  );
};

export default ProfileCompleteness;
